import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { updateUserShippingAddressAction } from "../../../redux/slices/users/usersSlice";
import ErrorMsg from "../../ErrorMessage/ErrorMsg";
import LoadingComponent from "../../LoadingComp/LoadingComponent";

export default function AddShippingAddress({ onSuccess, onCancel, initialData }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loading, error, profile } = useSelector((state) => state?.users);
  
  // Get initial data from props or profile
  const existingAddress = initialData || profile?.shippingAddress;
  
  const [formData, setFormData] = useState({
    nombre: "",
    paterno: "",
    direccion: "",
    colonia: "",
    delegacion: "",
    zipcode: "",
    estado: "",
    telefono: "",
  });
  
  // Populate form with existing data if available
  useEffect(() => {
    if (existingAddress && Object.keys(existingAddress).length > 0) {
      setFormData({
        nombre: existingAddress.nombre || "",
        paterno: existingAddress.paterno || "",
        direccion: existingAddress.direccion || "",
        colonia: existingAddress.colonia || "",
        delegacion: existingAddress.delegacion || "",
        zipcode: existingAddress.zipcode || "",
        estado: existingAddress.estado || "",
        telefono: existingAddress.telefono || "",
      });
    }
  }, [existingAddress]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const result = await dispatch(updateUserShippingAddressAction(formData));
    
    if (result.type === "users/update-shipping-address/fulfilled") {
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <ErrorMsg message={error?.message || t('addShippingAddress.error')} />}
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('addShippingAddress.firstName')}
          </label>
          <input
            type="text"
            name="nombre"
            value={formData.nombre}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('addShippingAddress.lastName')}
          </label>
          <input
            type="text"
            name="paterno"
            value={formData.paterno}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            required
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {t('addShippingAddress.address')}
        </label>
        <input
          type="text"
          name="direccion"
          value={formData.direccion}
          onChange={handleInputChange}
          className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {t('addShippingAddress.neighborhood')}
        </label>
        <input
          type="text"
          name="colonia"
          value={formData.colonia}
          onChange={handleInputChange}
          className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {t('addShippingAddress.municipality')}
        </label>
        <input
          type="text"
          name="delegacion"
          value={formData.delegacion}
          onChange={handleInputChange}
          className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('addShippingAddress.postalCode')}
          </label>
          <input
            type="text"
            name="zipcode"
            value={formData.zipcode}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('addShippingAddress.state')}
          </label>
          <input
            type="text"
            name="estado"
            value={formData.estado}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            required
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {t('addShippingAddress.phone')}
        </label>
        <input
          type="tel"
          name="telefono"
          value={formData.telefono}
          onChange={handleInputChange}
          className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
          required
        />
      </div>
      
      <div className="flex space-x-3">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 rounded-md bg-orange-600 py-2 px-4 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50"
        >
          {loading ? t('addShippingAddress.saving') : t('addShippingAddress.saveAddress')}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-md border border-gray-300 bg-white py-2 px-4 text-gray-700 hover:bg-gray-50"
          >
            {t('addShippingAddress.cancel')}
          </button>
        )}
      </div>
    </form>
  );
}